import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyPin } from "../services/api";
import { DeviceInfoResponse } from "../services/types";
import { useTranslation } from "../i18n";
import CameraVideo, { CameraVideoRef } from "./CameraVideo";
import "./GuestPass.css";

interface GuestPassProps {
  deviceId?: string;
  sessionToken?: string;
  guestId?: string;
  deviceInfo?: DeviceInfoResponse;
}

interface LocationState {
  deviceId?: string;
  sessionToken?: string;
  guestId?: string;
  deviceInfo?: DeviceInfoResponse;
}

const GuestPass: React.FC<GuestPassProps> = ({
  deviceId: propDeviceId,
  sessionToken: propSessionToken,
  guestId: propGuestId,
  deviceInfo: propDeviceInfo,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  // Use props first, then state as fallback
  const deviceId = propDeviceId || state?.deviceId;
  const sessionToken = propSessionToken || state?.sessionToken;
  const guestId = propGuestId || state?.guestId;
  const deviceInfo = propDeviceInfo || state?.deviceInfo;

  const [code, setCode] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { t, translate } = useTranslation();

  // Camera ref for photo capture
  const cameraRef = useRef<CameraVideoRef>(null);

  // State for captured photo
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>(null);

  // Photo capture handler
  const handlePhotoCapture = (photoBlob: Blob) => {
    setCapturedPhoto(photoBlob);
  };

  // Helper function to capture photo from camera
  const captureEventPhoto = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!cameraRef.current) {
        resolve(null);
        return;
      }

      let photoReceived = false;

      // Get the hidden video element
      const videoElement = document.querySelector(
        ".camera-video"
      ) as HTMLVideoElement;

      if (
        !videoElement ||
        !videoElement.videoWidth ||
        !videoElement.videoHeight
      ) {
        resolve(null);
        return;
      }

      try {
        // Directly capture from video element
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!context) {
          resolve(null);
          return;
        }

        // Set canvas dimensions to match video
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        // Draw current video frame to canvas
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob && !photoReceived) {
              photoReceived = true;
              resolve(blob);
            } else if (!photoReceived) {
              photoReceived = true;
              resolve(null);
            }
          },
          "image/jpeg",
          0.8 // Quality setting
        );

        // Timeout fallback after 2 seconds
        setTimeout(() => {
          if (!photoReceived) {
            photoReceived = true;
            resolve(null);
          }
        }, 2000);
      } catch (error) {
        console.error("Error capturing photo:", error);
        if (!photoReceived) {
          photoReceived = true;
          resolve(null);
        }
      }
    });
  };

  // 键盘按键配置
  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    [t.guestPass.clear, "0", t.guestPass.confirm],
  ];

  // 处理按键点击
  const handleKeyPress = (key: string) => {
    if (key === t.guestPass.clear || key === "Clear") {
      setCode("");
      setShowError(false);
      setErrorMessage("");
    } else if (key === t.guestPass.confirm || key === "Confirm") {
      if (code.length >= 4) {
        handleSubmit(code);
      } else {
        setErrorMessage(t.guestPass.errorMinDigits);
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    } else {
      if (code.length < 8) {
        // Allow up to 8 digits for guest pass
        setCode((prev) => prev + key);
        setShowError(false);
        setErrorMessage("");
      }
    }
  };

  // 处理提交
  const handleSubmit = async (guestPin: string) => {
    if (!sessionToken) {
      setErrorMessage(t.guestPass.sessionMissing);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    setIsLoading(true);
    try {
      // Capture photo before PIN verification only if enabled
      const eventPhoto = deviceInfo?.device?.eventPhotoCaptureEnabled
        ? await captureEventPhoto()
        : null;

      const response = await verifyPin(
        sessionToken,
        guestPin,
        "guest",
        undefined,
        eventPhoto || undefined
      );

      if (response.success && response.result?.success) {
        // Success - show door open message
        const guest = response.result.guest;
        const doorOpen = response.result.doorOpen;

        if (doorOpen?.success) {
          setErrorMessage(
            translate(t.guestPass.welcomeMessage, {
              name: guest?.firstName || "Guest",
            })
          );
          setShowError(false);
          setShowSuccess(true);

          // Navigate back after a short delay to show the success message
          setTimeout(() => {
            navigate(-1);
          }, 2000);
        } else {
          setErrorMessage(t.guestPass.doorFailedToOpen);
          setShowError(true);
          setTimeout(() => setShowError(false), 5000);
        }
      } else {
        // Check if this is a session expiration error
        const errorMsg = response.error || t.guestPass.invalidPin;

        if (
          response.statusCode === 401 ||
          errorMsg.includes("Session not found") ||
          errorMsg.includes("session expired") ||
          errorMsg.includes("Unauthorized") ||
          errorMsg.includes("Please refresh the page")
        ) {
          // Session expired - navigate back to home
          setErrorMessage(t.guestPass.sessionExpired);
          setShowError(true);

          // Navigate back to home after showing the message
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 2000);
        } else {
          setErrorMessage(errorMsg);
          setShowError(true);
          setCode(""); // Clear the code on error
          setTimeout(() => setShowError(false), 3000);
        }
      }
    } catch (error) {
      console.error("Guest pass verification failed:", error);
      setErrorMessage(t.guestPass.errorOccurred);
      setShowError(true);
      setCode(""); // Clear the code on error
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理返回
  const handleBack = () => {
    navigate(-1);
  };

  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key;

      if (key >= "0" && key <= "9") {
        handleKeyPress(key);
      } else if (key === "Enter") {
        handleKeyPress("Confirm");
      } else if (key === "Backspace" || key === "Delete") {
        handleKeyPress("Clear");
      } else if (key === "Escape") {
        handleBack();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [code]);

  // 阻止背景滚动
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div className="guest-pass-overlay">
      <div className="guest-pass-container">
        {/* Hidden camera for photo capture */}
        <div style={{ display: "none" }}>
          <CameraVideo
            ref={cameraRef}
            onPhotoCapture={handlePhotoCapture}
            onError={(error) => console.log("Camera error:", error)}
            onSuccess={() => console.log("Camera ready for photo capture")}
          />
        </div>

        {/* 头部 */}
        <div className="guest-pass-header">
          <h2 className="guest-pass-title">{t.guestPass.title}</h2>
          <button className="close-btn" onClick={handleBack}>
            ✕
          </button>
        </div>

        {/* 说明文字 */}
        <div className="guest-pass-description">
          <p>{t.guestPass.description}</p>
        </div>

        {/* 密码显示区域 */}
        <div className="code-display">
          <div className="code-dots">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className={`code-dot ${index < code.length ? "filled" : ""}`}
              />
            ))}
          </div>
          {showError && <div className="error-message">{errorMessage}</div>}
          {showSuccess && <div className="success-message">{errorMessage}</div>}
        </div>

        {/* 数字键盘 */}
        <div className="guest-pass-grid">
          {keys.map((row, rowIndex) => (
            <div key={rowIndex} className="guest-pass-row">
              {row.map((key) => (
                <button
                  key={key}
                  className={`guest-pass-key ${
                    key === t.guestPass.confirm || key === "Confirm"
                      ? "confirm-key"
                      : key === t.guestPass.clear || key === "Clear"
                      ? "clear-key"
                      : "number-key"
                  } ${isLoading ? "disabled" : ""}`}
                  onClick={() => handleKeyPress(key)}
                  disabled={isLoading}
                >
                  {(key === t.guestPass.confirm || key === "Confirm") &&
                  isLoading
                    ? "..."
                    : key}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* 提示信息 */}
        <div className="guest-pass-hint">
          <p>{t.guestPass.hint1}</p>
          <p>{t.guestPass.hint2}</p>
        </div>

        {/* 返回按钮 */}
        <div className="guest-pass-actions">
          <button
            className="back-button"
            onClick={handleBack}
            disabled={isLoading}
          >
            {t.guestPass.backToHome}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestPass;
